import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input } from '@angular/core';
import { DataConsentInterface } from './model/data-consent.model';
import { NgRedux, select } from '@angular-redux/store';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { SidenavService } from '../../services/sidenav.service';
import { Observable, Subscription } from 'rxjs';
import { SelectedVehicleState } from '../../../store/selected-vehicle-store/selected-vehicle-state.model';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { MatIcon } from '@angular/material/icon';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-data-consent',
    templateUrl: './data-consent.component.html',
    styleUrls: ['./data-consent.component.scss'],
    imports: [MatIcon, NgIf, NgFor, MatSlideToggle, MatTooltip, NgSwitch, NgSwitchCase, NgSwitchDefault, TranslatePipe]
})

export class DataConsentComponent implements OnInit, OnDestroy, OnChanges {
  /**
    *@property Used to set the brand name to the translations of the translate service
    *@type {object}
   **/
  public translateParams: object;

  /**
    * @property boolean that holds true if brand is toyota(TOY)
   **/
  public vehicleBrand: boolean;

  /**
   * @property Holds resolved brand name for UI translations
   **/
  public brandName: string;

  /**
   * @property Will contain data consents model
  */
  public model: DataConsentInterface;

  /**
   * @property Will contain vehicle details payload
  */
  @Input() vehicleDetails: object;

  /**
  *@arg selectedVehicleObservable to observe selectedVehicle observable
   **/
  @select('selectedVehicle') readonly selectedVehicleObservable: Observable<SelectedVehicleState>;
  /**
  *@arg selectedVehicleObservable to observe selectedVehicle observable
   **/
  @select(state => state.api.dataConsentsResponse) readonly dataConsentsResponse: Observable<any>;

  /**
   * @property Stores consentNames we need to alter from API response
  */
  private updatedConsentNames: { [key: string]: string } = {
    'B1': 'Connected Vehicle Notification Acknowledgement',
    'A1': 'Vehicle Connectivity Consent',
    'A2': 'Service Connect Dealer Communication',
    'A3': 'Insure Connect',
    'A4': 'Wi-Fi Consent',
    'A11': '21MM External Video Capture',
    'A15': 'SXM 360L',
    'A19': 'Apple Map EV Routing',
    'A21': '24MM External Video Capture',
    'allCommunication': 'All communication (AC) - 24MM',
    'geoLocationCommunication': 'Geo-location communication (GC) - 24MM',
    'voiceCommunication': 'Voice communication (VC) - 24MM',
    'pictureCommunication': 'External video sharing (PC) - 24MM'
  };

  /**
   * @property to store the vehicle subscriptions
   */
  private vehicleSubscription: Subscription;

  /**
  *@property Reference to the spinner service so it can be open or close in any feature of the screen
   **/
  public spinnerRef: SpinnerRef;

  /**
   * @property to store the data consent subscriptions
  */
  private dataConsentSubscription: Subscription;

  /**
   *@property apiObservable Property to observe the redux customer store
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;


  /**
   * @property Will store data consents array from API response
  */
  public consents: any[] = [];

  /**
   * @property Indicates the data consent screen is waiting for API response
   **/
  public isLoading = true;

  /**
   * @property Indicates the screen should show either empty/error state message
   **/
  public hasErrorOrEmpty = false;

  /**
   * @property Indicates the API failed and UI should show error message (not empty message)
   **/
  public isError = false;

  /**
   * @property Tracks the last processed VIN to avoid duplicate API calls
   **/
  private lastProcessedVin: string = null;

  constructor(
    public translate: TranslationService,
    public reduxState: NgRedux<AppState>,
    private sidenav: SidenavService,
    private spinner: SpinnerService,
    private orchestrationService: OrchestrationService
  ) { }

  /**
   * @function ngOnInit Initializes the Data Consent side nav and triggers API call on open
   * @return void
   **/
  ngOnInit() {
    this.dataConsentSubscription = this.dataConsentsResponse.subscribe(response => {
      const hasType = !!response && Object.prototype.hasOwnProperty.call(response, 'type');
      const hasPayload = !!response && Object.prototype.hasOwnProperty.call(response, 'payload');
      if (!response || (!hasType && !hasPayload)) {
        return;
      }

      this.closeSpinner();
      this.isLoading = false;
      if (response.type === 'error') {
        this.setEmptyOrErrorState(true);
        return;
      }
      this.isError = false;
      const rawConsents = Array.isArray(response.payload) ? response.payload : [];
      this.consents = rawConsents.map(consent => {
        if (this.updatedConsentNames.hasOwnProperty(consent.consentId)) {
          return {
            ...consent,
            consentName: this.updatedConsentNames[consent.consentId]
          };
        }
        return consent;
      });
      this.consents = this.sortConsentsByCustomOrder(this.consents);
      this.hasErrorOrEmpty = !this.consents || this.consents.length === 0;
    });

    const vehicle: any = this.getVehicleContext();
    if (vehicle) {
      this.loadDataConsentsForVehicle(vehicle);
    } else {
      this.setEmptyOrErrorState(false);
    }
  }

  /**
   * @function ngOnChanges triggers data consent API call when vehicleDetails input changes
   * @param changes SimpleChanges object from Angular
   * @return void
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vehicleDetails && changes.vehicleDetails.currentValue) {
      const vehicle: any = changes.vehicleDetails.currentValue;
      this.loadDataConsentsForVehicle(vehicle);
    }
  }

  /**
   * @function loadDataConsentsForVehicle triggers data consent API call whenever VIN changes
   * @param vehicle selected vehicle object
   * @return void
   **/
  private loadDataConsentsForVehicle(vehicle: any): void {
    const vin = vehicle && vehicle['vin'];
    if (!vin) {
      this.setEmptyOrErrorState(false);
      return;
    }
    // Avoid duplicate API calls for the same VIN
    if (this.lastProcessedVin === vin) {
      return;
    }
    this.lastProcessedVin = vin;
    
    this.setBrand(vehicle);
    this.setTranslateParams();
    this.resetViewState();
    this.spinnerRef = this.spinner.open();
    this.orchestrationService.getDataConsents(vehicle);
  }

  /**
   * @function consentState Map status values to icon state
   * @param {string} status consent status from API
   * @returns {'ok' | 'not-ok' | 'unknown'} mapped status for UI
   **/
  public consentState(status: string): 'ok' | 'not-ok' | 'unknown' {
    if (!status) {
      return 'unknown';
    }
    switch (status.toLowerCase()) {
      case 'accepted':
        return 'ok';
      case 'declined':
        return 'not-ok';
      default:
        return 'unknown';
    }
  }

  /**
   * @function setTranslateParams sets the translation params based on brand
   * @return void
   **/
  public setTranslateParams(): void {
    this.translateParams = {
      brand: this.brandName,
      brandSpecificName: this.translate.getCustomTranslation(
        this.vehicleBrand ? 'data-consent.label.customer' : 'data-consent.label.guest'
      )['value']
    };
  }

  /**
   * @function close closes the side nav
   * @return void
   **/
  public close(): void {
    if (this.sidenav && this.sidenav.close) {
      this.sidenav.close();
    }
  }

  /**
   * @function ngOnDestroy unsubscribes from active subscriptions and closes spinner if open
   * @return void
   **/
  ngOnDestroy() {
    if (this.vehicleSubscription) {
      this.vehicleSubscription.unsubscribe();
    }
    if (this.dataConsentSubscription) {
      this.dataConsentSubscription.unsubscribe();
    }
    this.closeSpinner();
  }

  /**
   * @function getVehicleContext Resolves vehicle details from sidenav payload or store
   * @returns {any} vehicle object if available
   **/
  private getVehicleContext(): any {
    if (this.vehicleDetails && Object.keys(this.vehicleDetails).length > 0) {
      return this.vehicleDetails;
    }
    const storeVehicle = this.reduxState.getState().selectedVehicle && this.reduxState.getState().selectedVehicle.vehicle;
    return storeVehicle ? storeVehicle : null;
  }

  /**
   * @function setBrand Normalizes and sets brand flags for Toyota/Lexus/Subaru
   * @param {any} vehicle vehicle object
   * @return void
   **/
  private setBrand(vehicle: any): void {
    const raw = vehicle ? (vehicle['brand'] || vehicle['brandCode'] || '') : '';
    const normalized = String(raw).toLowerCase();

    const isToyota = ['toy', 'toyota', 't'].includes(normalized);
    const isLexus = ['lex', 'lexus', 'l'].includes(normalized);
    const isSubaru = ['sub', 'subaru', 's'].includes(normalized);

    this.vehicleBrand = isToyota;
    this.brandName = isToyota ? 'Toyota' : isSubaru ? 'Subaru' : isLexus ? 'Lexus' : 'Lexus';
  }

  /**
   * @function resetViewState Resets view state when sidenav is opened
   * @return void
   **/
  private resetViewState(): void {
    this.setViewState({ isLoading: true, isError: false, hasErrorOrEmpty: false, clearConsents: true });
  }

  /**
   * @function setEmptyOrErrorState Sets empty/error state consistently to reduce duplication
   * @param {boolean} isErrorState when true show error message, otherwise show empty message
   * @return void
   **/
  private setEmptyOrErrorState(isErrorState: boolean): void {
    this.setViewState({ isLoading: false, isError: isErrorState, hasErrorOrEmpty: true, clearConsents: true });
  }

  /**
   * @function setViewState Single handler to update view state (loading/error/empty) and clear data when needed
   * @param {object} state view state configuration
   * @return void
   **/
  private setViewState(state: { isLoading: boolean; isError: boolean; hasErrorOrEmpty: boolean; clearConsents?: boolean }): void {
    if (state && state.clearConsents) {
      this.consents = [];
    }
    this.isLoading = state.isLoading;
    this.isError = state.isError;
    this.hasErrorOrEmpty = state.hasErrorOrEmpty;
  }

  /**
   * @function closeSpinner Closes spinner if it is open
   * @return void
   **/
  private closeSpinner(): void {
    if (this.spinnerRef) {
      this.spinnerRef.close();
      this.spinnerRef = null;
    }
  }

  /**
   * @function hasNumber checks if consentId has a number
   * @param {string} str consentId
   * @returns {boolean} true if consentId contains a digit
   **/
  private hasNumber(str: string): boolean {
    return /\d/.test(str);
  }
  /**
   * @function sortConsentsByCustomOrder Sorts consents in custom order:
   * @param {any[]} consents array of consent objects
   * @returns {any[]} sorted array of consents
   **/
  private sortConsentsByCustomOrder(consents: any[]): any[] {
    const customOrder: { [key: string]: number } = {
      'B1': 1,
      'A1': 2,
      'A2': 3,
      'A3': 4,
      'A4': 5,
      'A11': 6,
      'A15': 7,
      'A19': 8,
      'A21': 9,
      'allCommunication': 10,
      'geoLocationCommunication': 11,
      'pictureCommunication': 12,
      'voiceCommunication': 13
    };

    return consents.sort((a, b) => {
      const orderA = customOrder[a.consentId] || 999;
      const orderB = customOrder[b.consentId] || 999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return 0;
    });
  }
}
